import { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, KeyRound, Copy, Check, Eye, EyeOff, Loader2, ExternalLink } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import TabPageLayout from './TabPageLayout';
import EmptyState from './EmptyState';
import { SecondaryButton, SuccessButton, LayoutActionButton } from './Buttons';
import StandardModal from './StandardModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import StatusBadge from './StatusBadge';
import { BACKEND_URL } from '../config/api';
import { getAdminUserData, canManageTab } from '../utils/superAdmin';

const PLATFORM_API = `${BACKEND_URL}/api/v2/super-admin`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const emptyForm = { label: '', environment: 'Production', url: '', org_slug: '', username: '', password: '', notes: '' };

const copyText = async (text, message) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(message);
  } catch {
    toast.error('Could not copy to clipboard');
  }
};

const CredField = ({ label, value, secret = false, link = false, testId }) => {
  const [show, setShow] = useState(false);
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-50 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-sm text-gray-900 truncate font-mono" data-testid={testId}>
          {secret && !show ? '••••••••••' : value}
        </p>
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {secret && (
          <button
            onClick={() => setShow((s) => !s)}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={show ? 'Hide' : 'Show'}
            data-testid={`${testId}-toggle`}
          >
            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        )}
        {link && (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Open link"
            data-testid={`${testId}-open`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        <button
          onClick={() => copyText(value, `${label} copied`)}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label={`Copy ${label}`}
          data-testid={`${testId}-copy`}
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const SuperAdminDemoTab = () => {
  const me = getAdminUserData();
  const canManage = canManageTab(me, 'demo');

  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchCredentials = useCallback(async () => {
    try {
      const res = await axios.get(`${PLATFORM_API}/demo-credentials`, authHeaders());
      setCredentials(res.data.credentials || []);
    } catch {
      toast.error('Failed to load demo credentials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openModal = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error('Label is required'); return; }
    setIsSaving(true);
    try {
      await axios.post(`${PLATFORM_API}/demo-credentials`, form, authHeaders());
      toast.success('Demo credential added');
      setModalOpen(false);
      fetchCredentials();
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to save'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${PLATFORM_API}/demo-credentials/${toDelete.id}`, authHeaders());
      toast.success('Demo credential deleted');
      setToDelete(null);
      fetchCredentials();
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to delete'));
    } finally {
      setIsDeleting(false);
    }
  };

  const copyAll = (c) => {
    const lines = [
      `${c.label} — BookingFlows demo`,
      c.url ? `Login: ${c.url}` : null,
      c.org_slug ? `Org shortname: ${c.org_slug}` : null,
      c.username ? `Username: ${c.username}` : null,
      c.password ? `Password: ${c.password}` : null,
      c.notes ? `Notes: ${c.notes}` : null,
    ].filter(Boolean).join('\n');
    copyText(lines, 'Demo credentials copied');
  };

  return (
    <TabPageLayout
      title="Demo"
      subtitle="Demo accounts for walking new team members and prospects through BookingFlows"
      loading={loading}
      actions={
        canManage ? (
          <LayoutActionButton icon={Plus} onClick={() => openModal()} data-testid="demo-add-button">
            Add
          </LayoutActionButton>
        ) : undefined
      }
    >
      <div className="space-y-6" data-testid="superadmin-demo-tab">
        {credentials.length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title="No Demo Credentials"
            description="Add a demo account so the team has a working login to show prospects"
            actionLabel={canManage ? 'Add Demo Credentials' : undefined}
            onAction={canManage ? () => openModal() : undefined}
            showAction={canManage}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {credentials.map((cred) => (
              <div key={cred.id} className="card-soft flex flex-col overflow-hidden" data-testid={`demo-card-${cred.id}`}>
                <div className="p-5 pb-3 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-50">
                    <KeyRound className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-medium text-gray-900 tracking-tight truncate">{cred.label}</p>
                  </div>
                  <StatusBadge status={cred.environment} />
                </div>
                <div className="px-5 pb-3 flex-1">
                  <CredField label="Login URL" value={cred.url} link testId={`demo-url-${cred.id}`} />
                  <CredField label="Org shortname" value={cred.org_slug} testId={`demo-slug-${cred.id}`} />
                  <CredField label="Username" value={cred.username} testId={`demo-username-${cred.id}`} />
                  <CredField label="Password" value={cred.password} secret testId={`demo-password-${cred.id}`} />
                  {cred.notes && <p className="text-xs text-gray-500 pt-2.5 whitespace-pre-line">{cred.notes}</p>}
                </div>
                <div className={`grid ${canManage ? 'grid-cols-2' : 'grid-cols-1'} border-t border-gray-100`}>
                  <button
                    onClick={() => copyAll(cred)}
                    className="flex items-center justify-center gap-1.5 py-3 text-[13px] font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-50/70 transition-colors"
                    data-testid={`demo-copy-all-${cred.id}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy All
                  </button>
                  {canManage && (
                    <button
                      onClick={() => setToDelete(cred)}
                      className="flex items-center justify-center gap-1.5 py-3 text-[13px] font-medium text-red-400 hover:text-red-600 hover:bg-red-50/60 transition-colors border-l border-gray-100"
                      data-testid={`demo-delete-${cred.id}`}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <StandardModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Add Demo Credentials"
          size="md"
          footer={
            <>
              <SecondaryButton onClick={() => setModalOpen(false)} className="flex-1" disabled={isSaving}>Cancel</SecondaryButton>
              <SuccessButton onClick={handleSave} className="flex-1" disabled={isSaving} data-testid="demo-save-button">
                {isSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : (<><Check className="w-4 h-4 mr-2" />Add</>)}
              </SuccessButton>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <Label>Label <span className="text-red-500">*</span></Label>
              <Input value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="USA Demo — Lake Austin Calm" className="mt-1.5" data-testid="demo-label-input" />
            </div>
            <div>
              <Label>Environment</Label>
              <Select value={form.environment} onValueChange={(v) => set('environment', v)}>
                <SelectTrigger className="mt-1.5" data-testid="demo-environment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Production">Production</SelectItem>
                  <SelectItem value="Preview">Preview</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Login URL</Label>
              <Input value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://bookingflows.com/login" className="mt-1.5" data-testid="demo-url-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Org shortname</Label>
                <Input value={form.org_slug} onChange={(e) => set('org_slug', e.target.value)} placeholder="ryansboatcompany" className="mt-1.5" data-testid="demo-slug-input" />
              </div>
              <div>
                <Label>Username</Label>
                <Input value={form.username} onChange={(e) => set('username', e.target.value)} placeholder="demo@bookingflows.com" className="mt-1.5" data-testid="demo-username-input" />
              </div>
            </div>
            <div>
              <Label>Password</Label>
              <Input value={form.password} onChange={(e) => set('password', e.target.value)} className="mt-1.5" data-testid="demo-password-input" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="What this demo account is set up to show" className="mt-1.5" rows={3} data-testid="demo-notes-input" />
            </div>
          </div>
        </StandardModal>

        <DeleteConfirmModal
          isOpen={!!toDelete}
          onClose={() => setToDelete(null)}
          onConfirm={handleDelete}
          title="Delete Demo Credentials?"
          description={`Are you sure you want to delete "${toDelete?.label}"?`}
          isDeleting={isDeleting}
        >
          <p>The team will no longer see this demo account on the Demo tab. The demo account itself is not affected.</p>
        </DeleteConfirmModal>
      </div>
    </TabPageLayout>
  );
};

export default SuperAdminDemoTab;
